import {
  IntegrationFormField,
  useIntegrationFormContext,
} from 'modules/mobileApps/context/integrationForm';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { FileDropzone, FileDropzoneProps } from 'ui/fileDropzone';
import { FormField, FormFieldProps } from 'ui/formField';
import { WarningFill16 } from 'ui/icons';
import { colors } from 'ui/theme';
import { getFormErrorMessages } from '../../utils';
import { ErrorMessageContainer } from '../FirebaseIntegrationForm.styled';

const ErrorText = ({ message }: { message: string }) => (
  <ErrorMessageContainer>
    <WarningFill16 color={colors.iconDestructive} />
    {message}
  </ErrorMessageContainer>
);

type Props = Omit<FormFieldProps, 'children'> &
  Pick<FileDropzoneProps, 'accept' | 'onCancel'> &
  Partial<Pick<FileDropzoneProps, 'onUpload'>> & {
    name: IntegrationFormField;
  };

export const FileDropzoneField = ({
  accept,
  helpText,
  name,
  onCancel,
  onUpload,
  ...props
}: Props) => {
  const {
    clearErrors,
    formState: { errors },
    setError,
    setValue,
  } = useIntegrationFormContext();

  const errorMessages = getFormErrorMessages(errors);

  /** We are currently only handling the invalid file type error use case in the form validation outside the FileDropzone component.
   * Other errors (see `FileTooLarge`, `FileTooSmall`, `TooManyFiles`) have error states handled within the FileDropzone component. */
  const setFileError = (fileRejections?: FileRejection[]) => {
    if (fileRejections?.length) {
      const invalidTypeError = fileRejections[0].errors.find(
        (e) => e.code === ErrorCode.FileInvalidType,
      );
      if (invalidTypeError) setError(name, { message: 'Unsupported file type', type: 'validate' });
    } else clearErrors(name);
  };

  const handleCancel = () => {
    clearErrors(name);
    onCancel?.();
  };

  const handleUpload = (acceptedFiles: File[], fileRejections?: FileRejection[]) => {
    setValue(name, acceptedFiles[0]);
    setFileError(fileRejections);
    onUpload?.(acceptedFiles, fileRejections);
  };

  return (
    <FormField
      errorText={<ErrorText message={errorMessages[name]} />}
      hasError={!!errorMessages[name]}
      helpText={!errorMessages[name] && helpText}
      {...props}
    >
      <FileDropzone accept={accept} onCancel={handleCancel} onUpload={handleUpload} />
    </FormField>
  );
};
